import axios from 'axios'
import { getToken, logout } from '@/utils/auth.js'
import { RequestCancel } from '@/utils/request/requestCancel.js'
import { refreshToken } from '@/utils/request/refresh.js'
import { useAppStore } from '@/stores/index.js'
import { Message } from '@arco-design/web-vue'

let requestCancel = new RequestCancel()

// 白名单内的接口不需要token
let whiteList = ['/oauth/token']

// 是否正在刷新
let refreshing = false
// 待刷新的请求队列
let refreshQueue = []

const service = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
})

service.interceptors.request.use(
  config => {
    console.log(`请求地址【${config.url}】---入参---`, config.data || config.params)

    // 请求地址是否需要携带token
    let inWhiteList = whiteList.find(p => config.url.indexOf(p) > -1)
    if (!inWhiteList) {
      let token = getToken()

      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
      }
    }
    config.headers['Accept-Language'] = useAppStore().lang

    // 根据入参决定是否取消重复请求
    let repeat_request_cancel = config.repeat_request_cancel || true
    if (repeat_request_cancel) requestCancel.addPending(config, axios)

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  response => {
    if (response.config.data && typeof response.config.data === 'string') response.config.data = response.config.data.replace(/^"(.*)"$/, '$1')

    requestCancel.removePending(response.config)

    let res = response.data

    console.log(`请求地址【${response.config.url}】---结果---`, res)

    return res
  },
  async error => {
    if (error) {
      if (Object.hasOwnProperty.call(error, 'code') && error.code === 'ERR_CANCELED') {
        // 取消重复请求
        requestCancel.removePending(error)
        return Promise.reject(error)
      }

      if (error.config && error.config.data) error.config.data = error.config.data.replace(/^"(.*)"$/, '$1')
      let { config } = error

      requestCancel.removePending(config)

      if (refreshing && !config.url.includes('/oauth/token')) {
        return new Promise(resolve => {
          refreshQueue.push({ config: config, resolve: resolve })
        })
      }

      let errMsg = ''
      if (error.message.indexOf('timeout') !== -1) {
        errMsg = '连接超时'
      } else if (error.message === 'Network Error') {
        errMsg = '网络连接错误'
      } else if (error.status === 404) {
        errMsg = '未找到对应的接口'
      } else {
        errMsg = error.response.statusText

        let reGet = false
        if (error.response.status === 401 || error.response.status === 400) {
          let data = error.response.data
          if (data) {
            if (data.error_description && data.error_description.indexOf('Invalid access token') > -1) {
              reGet = true
            } else {
              errMsg = null
            }
          } else {
            reGet = true
          }
        } else if (error.response.status === 500) {
          errMsg = '接口返回信息错误'
        }

        if (reGet) {
          refreshing = true

          const res = await refreshToken(service)
          refreshing = false
          if (res && res.statusText === 'success') {
            refreshQueue.forEach(({ config, resolve }) => {
              resolve(service(config))
            })

            refreshQueue = []
            return service(config)
          } else {
            // 登录失效，需要重新登录
            Message.error('您的登录信息已失效，请重新登录')
            setTimeout(() => {
              requestCancel.clearPending()

              logout()
            }, 2000)
            return Promise.reject(error)
          }
        } else if (error.response.status === 400) {
          errMsg = ''
        }
      }

      if (errMsg) {
        Message.error(errMsg)
      }
    }
    return Promise.reject(error)
  }
)

export default service
